import { useMemo, useState } from "react";
import { useQuery, useMutation, useConvexAuth } from "convex/react";
import { ConvexError } from "convex/values";
import type { FunctionReturnType } from "convex/server";
import { api } from "../../../../convex/_generated/api";
import { GlassCard, PillTag, Btn, Avatar, SectionTitle } from "@/components/mnf/UI";
import Icon from "@/components/mnf/Icon";
import { UpsellCard } from "@/components/mnf/TierLock";
import { reportWriteFailure } from "@/lib/reportWriteFailure";
import { formatTime, relativePast } from "@/lib/datetime";

/**
 * The Gather → Community tab.
 *
 * PERSISTENCE: everything on this screen reads and writes Convex
 * (convex/community.ts). Before 2026-08-23 it read client/src/lib/store.ts —
 * an in-memory object — so a post, an Amen, a comment or an RSVP lived in one
 * browser tab, vanished on refresh, and no other sister ever saw it. The
 * server implementation had existed the whole time with zero call sites.
 *
 * Three rules this file follows, because this codebase has been burned by each:
 *  - `undefined` from useQuery means LOADING, not empty. Rendering "nothing
 *    here yet" over a query in flight is a lie that reads as a feature.
 *  - Every mutation is awaited inside try/catch and a failure is SAID OUT LOUD
 *    (reportWriteFailure). No control reports success for work that failed.
 *  - No seeded demo-account fallback for the viewer's id. The caller IS the
 *    session: the server derives identity, and the queries are skipped when
 *    there is no session rather than pointed at a stand-in account.
 */

type FeedItem = FunctionReturnType<typeof api.community.feedRecent>[number];
type EventItem = FunctionReturnType<typeof api.community.upcomingEvents>[number];
type FeedKind = FeedItem["kind"];
type EventSource = EventItem["source"];

const FEED_META: Record<FeedKind, { label: string; color: string; bg: string; icon: string; emoji: string }> = {
  milestone: { label: "Milestone", color: "var(--mnf-t)",      bg: "rgba(192,120,74,0.12)", icon: "celebration",  emoji: "🌸" },
  prayer:    { label: "Prayer",    color: "var(--mnf-purple)", bg: "var(--mnf-purpleL)",    icon: "menu_book",    emoji: "🙏" },
  verse:     { label: "Verse",     color: "#A07820",           bg: "rgba(160,120,32,0.10)", icon: "format_quote", emoji: "📖" },
  garden:    { label: "Garden",    color: "var(--mnf-f)",      bg: "rgba(30,74,53,0.08)",   icon: "eco",          emoji: "🌿" },
  note:      { label: "Note",      color: "var(--mnf-f)",      bg: "rgba(30,74,53,0.06)",   icon: "auto_stories", emoji: "✍️" },
};

const SOURCE_META: Record<EventSource, { label: string; emoji: string; color: string; bg: string }> = {
  coach:            { label: "Coach",          emoji: "📖", color: "var(--mnf-purple)", bg: "var(--mnf-purpleL)" },
  "sister-meetup":  { label: "Sister meetups", emoji: "🌱", color: "#2C6248",           bg: "var(--mnf-fiberBg)" },
  partner:          { label: "Partners",       emoji: "🥕", color: "var(--mnf-t)",      bg: "rgba(192,120,74,0.10)" },
};

const FEED_KINDS = Object.keys(FEED_META) as FeedKind[];
const EVENT_SOURCES = Object.keys(SOURCE_META) as EventSource[];

/** The `code` a Convex handler threw, when it threw a structured ConvexError. */
function errorCode(err: unknown): string | null {
  if (err instanceof ConvexError) {
    const data = err.data as { code?: unknown } | undefined;
    if (typeof data?.code === "string") return data.code;
  }
  return null;
}

type Viewer = { name?: string | null; tier?: string | null } | null | undefined;

export default function CommunityTab({
  me,
  canHostMeetup,
  onSeeRooted,
}: {
  me: Viewer;
  canHostMeetup: boolean;
  onSeeRooted: () => void;
}) {
  const { isAuthenticated, isLoading: authLoading } = useConvexAuth();

  // Convex throws NOT_AUTHORIZED on these without a session, and a thrown
  // query surfaces as a render error (the app's Sentry boundary → crash
  // screen). Skipping is what keeps a signed-out render from taking the page
  // down. Routes are gated today, but a session can also expire mid-visit.
  const args = isAuthenticated ? {} : ("skip" as const);
  const posts = useQuery(api.community.feedRecent, args);
  const events = useQuery(api.community.upcomingEvents, args);
  const myRsvps = useQuery(api.community.myRsvps, args);
  const hostedMeetups = useQuery(api.community.myHostedMeetups, args);

  const [feedFilter, setFeedFilter] = useState<FeedKind | "all">("all");
  const [sourceFilter, setSourceFilter] = useState<EventSource | "all">("all");

  // Above the early return: hook order must not depend on auth state.
  // `null` means "still loading", which is NOT the same as "not going".
  const rsvpedIds = useMemo(
    () => (myRsvps === undefined ? null : new Set(myRsvps.map((r) => r.eventId))),
    [myRsvps],
  );

  if (!isAuthenticated) {
    return (
      <GlassCard padding={20}>
        <p className="mnf-body italic text-center" style={{ fontSize: 13, color: "var(--mnf-tg)" }}>
          {authLoading
            ? "Finding your seat at the table..."
            : "Sign in to see what the sisters are sharing."}
        </p>
      </GlassCard>
    );
  }

  const filteredFeed =
    posts === undefined
      ? undefined
      : feedFilter === "all"
        ? posts
        : posts.filter((p) => p.kind === feedFilter);

  const filteredEvents =
    events === undefined
      ? undefined
      : sourceFilter === "all"
        ? events
        : events.filter((e) => e.source === sourceFilter);

  return (
    <>
      {/* ----------------------------- Feed ----------------------------- */}
      <div>
        <SectionTitle
          eyebrow="Sister voices"
          title="What we're sharing"
          trailing={
            <span>
              {posts === undefined ? "loading" : `${posts.length} ${posts.length === 1 ? "post" : "posts"}`}
            </span>
          }
        />
        <div className="flex gap-2 overflow-x-auto -mx-5 px-5 py-3" style={{ scrollbarWidth: "none" }}>
          <FilterPill active={feedFilter === "all"} onClick={() => setFeedFilter("all")} emoji="✿" label="All" />
          {FEED_KINDS.map((k) => (
            <FilterPill
              key={k}
              active={feedFilter === k}
              onClick={() => setFeedFilter(k)}
              emoji={FEED_META[k].emoji}
              label={FEED_META[k].label}
            />
          ))}
        </div>

        <Composer me={me} onPosted={() => setFeedFilter("all")} />

        {filteredFeed === undefined ? (
          <LoadingCard text="Gathering sister voices..." />
        ) : filteredFeed.length === 0 ? (
          <GlassCard padding={20} className="mt-3">
            <p className="mnf-body italic text-center" style={{ fontSize: 13, color: "var(--mnf-tg)" }}>
              {feedFilter === "all"
                ? "Nothing shared yet. Be the first, sister."
                : "Nothing in this thread yet. Be the first to share."}
            </p>
          </GlassCard>
        ) : (
          <div className="space-y-3 mt-1">
            {filteredFeed.map((p) => (
              <FeedPostCard key={p.id} post={p} />
            ))}
          </div>
        )}
      </div>

      {/* --------------------------- Gatherings -------------------------- */}
      <div className="pt-2">
        <SectionTitle
          eyebrow="Coming up"
          title="What's growing together"
          trailing={
            <span>
              {events === undefined ? "loading" : `${events.length} ${events.length === 1 ? "gathering" : "gatherings"}`}
            </span>
          }
        />
        <div className="flex gap-2 overflow-x-auto -mx-5 px-5 py-3" style={{ scrollbarWidth: "none" }}>
          <FilterPill active={sourceFilter === "all"} onClick={() => setSourceFilter("all")} emoji="✿" label="All" />
          {EVENT_SOURCES.map((s) => (
            <FilterPill
              key={s}
              active={sourceFilter === s}
              onClick={() => setSourceFilter(s)}
              emoji={SOURCE_META[s].emoji}
              label={SOURCE_META[s].label}
            />
          ))}
        </div>

        {filteredEvents === undefined ? (
          <LoadingCard text="Looking for gatherings..." />
        ) : filteredEvents.length === 0 ? (
          <GlassCard padding={20}>
            <p className="mnf-body italic text-center" style={{ fontSize: 13, color: "var(--mnf-tg)" }}>
              Nothing on the calendar in this category yet. Try another, or host a sister meetup below.
            </p>
          </GlassCard>
        ) : (
          <div className="space-y-3">
            {filteredEvents.map((e) => (
              <CommunityCard key={e.id} event={e} rsvpedIds={rsvpedIds} />
            ))}
          </div>
        )}
      </div>

      {/* ---------------------------- Hosting ---------------------------- */}
      {canHostMeetup ? (
        <HostMeetupCard hosted={hostedMeetups} />
      ) : (
        <UpsellCard
          tier="Rooted"
          eyebrow="Sisterhood in person"
          title="Host a sister meetup"
          description="Rooted sisters can plant local meetups, walks, prayer circles, and shared kitchens within a few miles of home."
          ctaLabel="See Rooted"
          onCta={onSeeRooted}
          icon="diversity_3"
        />
      )}
    </>
  );
}

/* ------------------------------- Composer -------------------------------- */

function Composer({ me, onPosted }: { me: Viewer; onPosted: () => void }) {
  const createFeedPost = useMutation(api.community.createFeedPost);
  const [open, setOpen] = useState(false);
  const [kind, setKind] = useState<FeedKind>("note");
  const [body, setBody] = useState("");
  const [verseRef, setVerseRef] = useState("");
  const [saving, setSaving] = useState(false);

  const initials =
    me?.name?.split(" ").map((p) => p[0]).join("").slice(0, 2).toUpperCase() ?? "S";

  async function submit() {
    const text = body.trim();
    if (!text || saving) return;
    setSaving(true);
    try {
      await createFeedPost({
        kind,
        body: text,
        ...(kind === "verse" && verseRef.trim() ? { verseRef: verseRef.trim() } : {}),
      });
      // Only clear the box once the server has it. On failure her words stay
      // where she typed them.
      setBody("");
      setVerseRef("");
      setOpen(false);
      onPosted();
    } catch (err) {
      reportWriteFailure(
        "community",
        "sharing your post",
        err,
        "Your post didn't send",
        errorCode(err) === "NOT_AUTHORIZED"
          ? "We couldn't confirm you're signed in. Your words are still here — reload and try again."
          : "Nothing was saved. Your words are still in the box — check your connection and try again.",
      );
    } finally {
      setSaving(false);
    }
  }

  if (!open) {
    return (
      <GlassCard padding={12} className="mb-3">
        <div className="flex items-center gap-3">
          <Avatar initials={initials} size={36} ringColor="rgba(192,120,74,0.4)" />
          <button
            data-testid="button-compose-feed"
            onClick={() => setOpen(true)}
            className="flex-1 text-left mnf-body"
            style={{
              background: "rgba(245,240,232,0.7)",
              border: "1px solid rgba(30,74,53,0.08)",
              borderRadius: 999,
              padding: "10px 14px",
              fontSize: 12.5,
              color: "var(--mnf-tg)",
              fontStyle: "italic",
              cursor: "pointer",
            }}
          >
            Share a milestone, prayer, or verse, sister...
          </button>
        </div>
      </GlassCard>
    );
  }

  return (
    <GlassCard padding={14} className="mb-3">
      <div className="flex items-center gap-2 flex-wrap mb-3">
        {FEED_KINDS.map((k) => (
          <FilterPill
            key={k}
            active={kind === k}
            onClick={() => setKind(k)}
            emoji={FEED_META[k].emoji}
            label={FEED_META[k].label}
          />
        ))}
      </div>
      <textarea
        value={body}
        onChange={(e) => setBody(e.target.value)}
        rows={4}
        maxLength={2000}
        autoFocus
        aria-label="What would you like to share?"
        placeholder="What has He grown in you this week?"
        data-testid="input-feed-body"
        className="mnf-body w-full"
        style={{
          background: "rgba(245,240,232,0.7)",
          border: "1px solid rgba(30,74,53,0.10)",
          borderRadius: 14,
          padding: "10px 12px",
          fontSize: 13,
          color: "var(--mnf-tx)",
          resize: "vertical",
        }}
      />
      {kind === "verse" && (
        <input
          value={verseRef}
          onChange={(e) => setVerseRef(e.target.value)}
          maxLength={80}
          aria-label="Verse reference"
          placeholder="Reference, e.g. Philippians 4:13"
          data-testid="input-feed-verse"
          className="mnf-body w-full mt-2"
          style={{
            background: "rgba(245,240,232,0.7)",
            border: "1px solid rgba(30,74,53,0.10)",
            borderRadius: 999,
            padding: "9px 14px",
            fontSize: 12.5,
            color: "var(--mnf-tx)",
          }}
        />
      )}
      <div className="flex items-center gap-2 mt-3">
        <Btn
          variant="ghost"
          size="sm"
          onClick={() => { setOpen(false); }}
          disabled={saving}
          data-testid="button-compose-cancel"
        >
          Cancel
        </Btn>
        <Btn
          variant="coral"
          size="sm"
          block
          onClick={submit}
          disabled={saving || body.trim().length === 0}
          data-testid="button-compose-share"
        >
          <Icon name="send" size={14} /> {saving ? "Sharing..." : "Share"}
        </Btn>
      </div>
    </GlassCard>
  );
}

/* ------------------------------- Feed card ------------------------------- */

function FeedPostCard({ post }: { post: FeedItem }) {
  const bloomPost = useMutation(api.community.bloomPost);
  const [pending, setPending] = useState(false);
  const [showComments, setShowComments] = useState(false);
  const meta = FEED_META[post.kind];

  async function toggleBloom() {
    if (pending) return;
    setPending(true);
    try {
      await bloomPost({ postId: post.id });
    } catch (err) {
      reportWriteFailure(
        "community",
        "saving your Amen",
        err,
        "Your Amen didn't save",
        "We couldn't reach the garden. Nothing changed — try again in a moment.",
      );
    } finally {
      setPending(false);
    }
  }

  return (
    <GlassCard padding={16} data-testid={`feed-post-${post.id}`}>
      <div className="flex items-start gap-3">
        <Avatar
          initials={post.authorInitials}
          size={40}
          ringColor={post.authorIsCoach ? "rgba(192,120,74,0.6)" : "rgba(30,74,53,0.18)"}
        />
        <div className="flex-1 min-w-0">
          <div className="flex items-center justify-between gap-2 mb-0.5">
            <div className="flex items-center gap-1.5 min-w-0">
              <p className="mnf-body truncate" style={{ fontSize: 13, fontWeight: 700, color: "var(--mnf-fd)" }}>
                {post.authorName}
              </p>
              {post.authorIsCoach && (
                <span
                  className="text-[9px] mnf-body uppercase"
                  style={{
                    padding: "2px 6px",
                    borderRadius: 999,
                    background: "rgba(192,120,74,0.14)",
                    color: "var(--mnf-t)",
                    letterSpacing: "0.08em",
                    fontWeight: 700,
                  }}
                >
                  Coach
                </span>
              )}
            </div>
            <span className="text-[10.5px] mnf-body" style={{ color: "var(--mnf-tg)", flexShrink: 0 }}>
              {relativePast(post.sentAt)}
            </span>
          </div>
          <div className="flex items-center gap-1.5 mb-2">
            <span
              className="inline-flex items-center gap-1 text-[10px] mnf-body uppercase"
              style={{
                padding: "2px 7px",
                borderRadius: 999,
                background: meta.bg,
                color: meta.color,
                letterSpacing: "0.08em",
                fontWeight: 700,
              }}
            >
              <Icon name={meta.icon} size={10} /> {meta.label}
            </span>
            {/* Only when we actually know her tier — no invented badge. */}
            {post.authorTier && (
              <span className="text-[10.5px] mnf-body" style={{ color: "var(--mnf-tg)" }}>
                {post.authorTier} sister
              </span>
            )}
          </div>

          {post.kind === "verse" ? (
            <>
              <p className="mnf-serif italic" style={{ fontSize: 15, color: "var(--mnf-fd)", lineHeight: 1.45 }}>
                "{post.body}"
              </p>
              {post.verseRef && (
                <p
                  className="mnf-body mt-1"
                  style={{ fontSize: 11.5, color: meta.color, fontWeight: 600, letterSpacing: "0.04em" }}
                >
                  {post.verseRef}
                </p>
              )}
            </>
          ) : (
            <p className="mnf-body" style={{ fontSize: 13, color: "var(--mnf-tx)", lineHeight: 1.5 }}>
              {post.body}
            </p>
          )}

          <div className="flex items-center gap-1 mt-3 -ml-1">
            <FeedAction
              icon={post.bloomedByMe ? "favorite" : "favorite_border"}
              label="Amen"
              count={post.blooms}
              active={post.bloomedByMe}
              activeColor="var(--mnf-t)"
              disabled={pending}
              onClick={toggleBloom}
              testid={`button-amen-${post.id}`}
            />
            <FeedAction
              icon="chat_bubble_outline"
              label="Reply"
              count={post.commentCount}
              active={showComments}
              activeColor="var(--mnf-f)"
              onClick={() => setShowComments((s) => !s)}
              testid={`button-reply-${post.id}`}
            />
          </div>

          {showComments && <CommentThread postId={post.id} />}
        </div>
      </div>
    </GlassCard>
  );
}

/* ----------------------------- Comment thread ---------------------------- */

function CommentThread({ postId }: { postId: string }) {
  // Mounted only while the thread is open, so there is nothing to skip: the
  // parent already established a session.
  const comments = useQuery(api.community.feedComments, { postId });
  const addFeedComment = useMutation(api.community.addFeedComment);
  const [draft, setDraft] = useState("");
  const [saving, setSaving] = useState(false);

  async function submit() {
    const text = draft.trim();
    if (!text || saving) return;
    setSaving(true);
    try {
      await addFeedComment({ postId, body: text });
      setDraft("");
    } catch (err) {
      reportWriteFailure(
        "community",
        "saving your reply",
        err,
        "Your reply didn't send",
        "Nothing was saved. Your words are still in the box — check your connection and try again.",
      );
    } finally {
      setSaving(false);
    }
  }

  return (
    <div
      className="mt-3 pt-3"
      style={{ borderTop: "1px solid rgba(30,74,53,0.08)" }}
      data-testid={`comments-${postId}`}
    >
      {comments === undefined ? (
        <p className="mnf-body italic" style={{ fontSize: 12, color: "var(--mnf-tg)" }}>
          Loading replies...
        </p>
      ) : comments.length === 0 ? (
        <p className="mnf-body italic" style={{ fontSize: 12, color: "var(--mnf-tg)" }}>
          No replies yet. Say something kind.
        </p>
      ) : (
        <div className="space-y-2.5">
          {comments.map((c) => (
            <div key={c.id} className="flex items-start gap-2">
              <Avatar initials={c.authorInitials} size={26} ringColor="rgba(30,74,53,0.14)" />
              <div className="flex-1 min-w-0">
                <div className="flex items-baseline gap-2">
                  <p className="mnf-body truncate" style={{ fontSize: 12, fontWeight: 700, color: "var(--mnf-fd)" }}>
                    {c.authorName}
                  </p>
                  <span className="text-[10px] mnf-body" style={{ color: "var(--mnf-tg)" }}>
                    {relativePast(c.sentAt)}
                  </span>
                </div>
                <p className="mnf-body" style={{ fontSize: 12.5, color: "var(--mnf-tx)", lineHeight: 1.45 }}>
                  {c.body}
                </p>
              </div>
            </div>
          ))}
        </div>
      )}

      <div className="flex items-center gap-2 mt-3">
        <input
          value={draft}
          onChange={(e) => setDraft(e.target.value)}
          onKeyDown={(e) => { if (e.key === "Enter") { e.preventDefault(); void submit(); } }}
          maxLength={2000}
          aria-label="Write a reply"
          placeholder="Write a reply..."
          data-testid={`input-comment-${postId}`}
          className="mnf-body flex-1 min-w-0"
          style={{
            background: "rgba(245,240,232,0.7)",
            border: "1px solid rgba(30,74,53,0.10)",
            borderRadius: 999,
            padding: "8px 13px",
            fontSize: 12.5,
            color: "var(--mnf-tx)",
          }}
        />
        <Btn
          variant="secondary"
          size="sm"
          onClick={submit}
          disabled={saving || draft.trim().length === 0}
          data-testid={`button-comment-send-${postId}`}
        >
          {saving ? "..." : "Send"}
        </Btn>
      </div>
    </div>
  );
}

/* ------------------------------ Event card ------------------------------- */

function CommunityCard({
  event,
  rsvpedIds,
}: {
  event: EventItem;
  /** null while the RSVP list is still loading — do NOT claim "not going". */
  rsvpedIds: Set<string> | null;
}) {
  const toggleEventRsvp = useMutation(api.community.toggleEventRsvp);
  const [pending, setPending] = useState(false);
  const meta = SOURCE_META[event.source];
  const going = rsvpedIds?.has(event.id) ?? false;
  const full = !going && event.rsvps >= event.capacity;

  async function toggle() {
    if (pending || rsvpedIds === null) return;
    setPending(true);
    try {
      await toggleEventRsvp({ eventId: event.id });
    } catch (err) {
      const code = errorCode(err);
      reportWriteFailure(
        "community",
        "saving your RSVP",
        err,
        code === "EVENT_FULL" ? "This gathering is full" : "Your RSVP didn't save",
        code === "EVENT_FULL"
          ? "Every seat is taken. Nothing was booked for you."
          : "Nothing changed. Check your connection and try again.",
      );
    } finally {
      setPending(false);
    }
  }

  const label = pending
    ? "..."
    : rsvpedIds === null
      ? "RSVP"
      : going
        ? `Going · ${event.rsvps}`
        : full
          ? "Full"
          : `RSVP · ${event.rsvps}`;

  return (
    <GlassCard padding={16} data-testid={`event-${event.id}`}>
      <div className="flex items-start gap-3">
        <div
          className="flex items-center justify-center shrink-0"
          style={{
            width: 44, height: 44,
            borderRadius: "16px 6px 16px 6px",
            background: meta.bg,
            color: meta.color,
            fontSize: 22,
          }}
          aria-hidden
        >
          {meta.emoji}
        </div>
        <div className="flex-1 min-w-0">
          <div className="flex items-center justify-between gap-2 mb-1">
            <p className="mnf-eyebrow" style={{ color: meta.color }}>{meta.label}</p>
            <span className="mnf-body" style={{ fontSize: 10.5, color: "var(--mnf-tg)" }}>
              {event.rsvps}/{event.capacity} going
            </span>
          </div>
          <h4 className="mnf-serif" style={{ fontSize: 16, fontWeight: 500, color: "var(--mnf-fd)", lineHeight: 1.25 }}>
            {event.title}
          </h4>
          <p className="mnf-body" style={{ fontSize: 11.5, color: "var(--mnf-tg)", marginTop: 3, lineHeight: 1.4 }}>
            {formatTime(event.startsAt)} · {event.durationMin} min
          </p>
          <p className="mnf-body" style={{ fontSize: 11.5, color: "var(--mnf-tg)", lineHeight: 1.4 }}>
            <Icon name="place" size={11} /> {event.location}
          </p>
          {event.notes && (
            <p className="mnf-body italic" style={{ fontSize: 12, color: "var(--mnf-tg)", marginTop: 6, lineHeight: 1.45 }}>
              {event.notes}
            </p>
          )}
          <div className="flex items-center gap-2 mt-3">
            {event.source === "sister-meetup" && (
              <span className="inline-flex items-center gap-1.5">
                <Avatar initials={event.hostInitials} size={22} />
                <span className="mnf-body" style={{ fontSize: 11, color: "var(--mnf-tg)" }}>
                  Hosted by {event.hostName}
                </span>
              </span>
            )}
            <button
              onClick={toggle}
              disabled={pending || full || rsvpedIds === null}
              data-testid={`button-rsvp-${event.id}`}
              aria-pressed={going}
              className="mnf-body ml-auto transition-all hover-elevate"
              style={{
                padding: "7px 14px", borderRadius: 999,
                background: going ? "var(--mnf-f)" : meta.color,
                color: "#fff",
                fontSize: 10.5, fontWeight: 700, letterSpacing: "0.08em", textTransform: "uppercase",
                border: "none",
                cursor: pending || full ? "default" : "pointer",
                opacity: full || rsvpedIds === null ? 0.55 : 1,
              }}
            >
              {label}
            </button>
          </div>
        </div>
      </div>
    </GlassCard>
  );
}

/* ------------------------------ Host meetup ------------------------------ */

/** Local datetime-local value → epoch ms, or null when unparseable. */
function parseLocalDateTime(value: string): number | null {
  if (!value) return null;
  const ms = new Date(value).getTime();
  return Number.isFinite(ms) ? ms : null;
}

function HostMeetupCard({
  hosted,
}: {
  hosted: FunctionReturnType<typeof api.community.myHostedMeetups> | undefined;
}) {
  const hostSisterMeetup = useMutation(api.community.hostSisterMeetup);
  const [open, setOpen] = useState(false);
  const [title, setTitle] = useState("");
  const [when, setWhen] = useState("");
  const [durationMin, setDurationMin] = useState("60");
  const [location, setLocation] = useState("");
  const [capacity, setCapacity] = useState("12");
  const [notes, setNotes] = useState("");
  const [saving, setSaving] = useState(false);
  const [problem, setProblem] = useState<string | null>(null);

  async function submit() {
    if (saving) return;
    const startsAt = parseLocalDateTime(when);
    const dur = Number.parseInt(durationMin, 10);
    const cap = Number.parseInt(capacity, 10);

    if (!title.trim()) return setProblem("Give your meetup a name.");
    if (startsAt === null) return setProblem("Pick a date and time.");
    if (startsAt <= Date.now()) return setProblem("Pick a time in the future.");
    if (!location.trim()) return setProblem("Where are you meeting?");
    if (!Number.isInteger(dur) || dur < 5 || dur > 600) return setProblem("Length should be 5 to 600 minutes.");
    if (!Number.isInteger(cap) || cap < 1 || cap > 500) return setProblem("Room for 1 to 500 sisters.");
    setProblem(null);

    setSaving(true);
    try {
      await hostSisterMeetup({
        title: title.trim(),
        startsAt,
        durationMin: dur,
        location: location.trim(),
        capacity: cap,
        ...(notes.trim() ? { notes: notes.trim() } : {}),
      });
      setTitle(""); setWhen(""); setLocation(""); setNotes("");
      setDurationMin("60"); setCapacity("12");
      setOpen(false);
    } catch (err) {
      const code = errorCode(err);
      reportWriteFailure(
        "community",
        "planting your meetup",
        err,
        "Your meetup wasn't created",
        code === "UPGRADE_REQUIRED"
          ? "Hosting is a Rooted feature. Nothing was created."
          : "Nothing was saved. Your details are still here — check your connection and try again.",
      );
    } finally {
      setSaving(false);
    }
  }

  return (
    <GlassCard
      organic
      padding={18}
      style={{
        background: "linear-gradient(135deg, rgba(192,120,74,0.14) 0%, rgba(245,240,232,0.65) 100%)",
        border: "1px solid rgba(192,120,74,0.30)",
      }}
    >
      <div className="flex items-start gap-3">
        <div
          className="flex items-center justify-center shrink-0"
          style={{
            width: 44, height: 44,
            borderRadius: "16px 6px 16px 6px",
            background: "var(--mnf-t)", color: "#fff",
          }}
        >
          <Icon name="diversity_3" size={20} weight={500} />
        </div>
        <div className="flex-1 min-w-0">
          <p className="mnf-eyebrow" style={{ color: "var(--mnf-t)" }}>Sisterhood in person</p>
          <p className="mnf-serif" style={{ fontSize: 17, fontWeight: 500, color: "var(--mnf-fd)", lineHeight: 1.3, marginTop: 3 }}>
            Host a sister meetup
          </p>
          <p className="mnf-body" style={{ fontSize: 12.5, color: "var(--mnf-tg)", lineHeight: 1.45, marginTop: 4 }}>
            Invite sisters within a few miles for a walk, prayer circle, or shared kitchen.
          </p>

          {/* What she has already planted. Loading is not "none". */}
          {hosted === undefined ? (
            <p className="mnf-body italic mt-3" style={{ fontSize: 11.5, color: "var(--mnf-tg)" }}>
              Checking your meetups...
            </p>
          ) : hosted.length > 0 && (
            <div className="mt-3 space-y-1.5" data-testid="hosted-meetups">
              <p className="mnf-eyebrow" style={{ color: "var(--mnf-f)" }}>Yours</p>
              {hosted.map((m) => (
                <div key={m.id} className="flex items-baseline justify-between gap-2">
                  <span className="mnf-body truncate" style={{ fontSize: 12.5, color: "var(--mnf-fd)", fontWeight: 600 }}>
                    {m.title}
                  </span>
                  <span className="mnf-body shrink-0" style={{ fontSize: 10.5, color: "var(--mnf-tg)" }}>
                    {formatTime(m.startsAt)} · {m.rsvps}/{m.capacity}
                  </span>
                </div>
              ))}
            </div>
          )}

          {!open ? (
            <Btn variant="coral" size="sm" className="mt-3" onClick={() => setOpen(true)} data-testid="button-host-meetup">
              <Icon name="add" size={14} /> Plant a meetup
            </Btn>
          ) : (
            <div className="mt-3 space-y-2">
              <MeetupInput value={title} onChange={setTitle} placeholder="Saturday morning prayer walk" label="Meetup name" testid="input-meetup-title" />
              <MeetupInput value={when} onChange={setWhen} type="datetime-local" placeholder="" label="When" testid="input-meetup-when" />
              <div className="flex gap-2">
                <MeetupInput value={durationMin} onChange={setDurationMin} type="number" placeholder="60" label="Minutes" testid="input-meetup-duration" />
                <MeetupInput value={capacity} onChange={setCapacity} type="number" placeholder="12" label="Room for" testid="input-meetup-capacity" />
              </div>
              <MeetupInput value={location} onChange={setLocation} placeholder="Percy Warner Park, Nashville" label="Where" testid="input-meetup-location" />
              <MeetupInput value={notes} onChange={setNotes} placeholder="Bring water and a verse to share." label="Notes" testid="input-meetup-notes" />

              {problem && (
                <p className="mnf-body" style={{ fontSize: 11.5, color: "#B3402A" }} role="alert">
                  {problem}
                </p>
              )}

              <div className="flex items-center gap-2 pt-1">
                <Btn variant="ghost" size="sm" onClick={() => { setOpen(false); setProblem(null); }} disabled={saving} data-testid="button-meetup-cancel">
                  Cancel
                </Btn>
                <Btn variant="coral" size="sm" block onClick={submit} disabled={saving} data-testid="button-meetup-save">
                  {saving ? "Planting..." : "Plant it"}
                </Btn>
              </div>
            </div>
          )}
        </div>
      </div>
    </GlassCard>
  );
}

function MeetupInput({
  value, onChange, placeholder, label, testid, type = "text",
}: {
  value: string;
  onChange: (v: string) => void;
  placeholder: string;
  label: string;
  testid: string;
  type?: "text" | "number" | "datetime-local";
}) {
  return (
    <input
      value={value}
      onChange={(e) => onChange(e.target.value)}
      type={type}
      placeholder={placeholder}
      aria-label={label}
      data-testid={testid}
      className="mnf-body w-full min-w-0"
      style={{
        background: "rgba(255,255,255,0.75)",
        border: "1px solid rgba(30,74,53,0.12)",
        borderRadius: 12,
        padding: "9px 12px",
        fontSize: 12.5,
        color: "var(--mnf-tx)",
      }}
    />
  );
}

/* --------------------------------- Bits ---------------------------------- */

function LoadingCard({ text }: { text: string }) {
  return (
    <GlassCard padding={20} className="mt-3">
      <p className="mnf-body italic text-center" style={{ fontSize: 13, color: "var(--mnf-tg)" }}>
        {text}
      </p>
    </GlassCard>
  );
}

function FilterPill({
  active, onClick, emoji, label,
}: { active: boolean; onClick: () => void; emoji: string; label: string }) {
  return (
    <button
      onClick={onClick}
      data-testid={`filter-${label.toLowerCase().replace(/\s+/g, "-")}`}
      className="mnf-body shrink-0 transition-all"
      style={{
        padding: "8px 14px", borderRadius: 999,
        background: active ? "var(--mnf-f)" : "rgba(255,255,255,0.65)",
        color: active ? "#F5F0E8" : "var(--mnf-fd)",
        border: `1px solid ${active ? "var(--mnf-f)" : "rgba(30,74,53,0.10)"}`,
        fontSize: 12, fontWeight: 600, letterSpacing: "0.04em",
        cursor: "pointer",
      }}
    >
      <span style={{ marginRight: 6 }}>{emoji}</span>{label}
    </button>
  );
}

function FeedAction({
  icon, label, count, active, activeColor, disabled, onClick, testid,
}: {
  icon: string;
  label: string;
  count: number;
  active: boolean;
  activeColor: string;
  disabled?: boolean;
  onClick: () => void;
  testid: string;
}) {
  return (
    <button
      onClick={onClick}
      disabled={disabled}
      data-testid={testid}
      className="inline-flex items-center gap-1.5 mnf-body transition-all hover-elevate"
      style={{
        padding: "6px 10px",
        borderRadius: 999,
        background: active ? `color-mix(in srgb, ${activeColor} 12%, transparent)` : "transparent",
        color: active ? activeColor : "var(--mnf-tg)",
        fontSize: 11.5,
        fontWeight: 600,
        border: "none",
        cursor: disabled ? "default" : "pointer",
        opacity: disabled ? 0.6 : 1,
      }}
      aria-pressed={active}
    >
      <Icon name={icon} size={14} />
      <span>{count}</span>
      <span className="hidden sm:inline">{label}</span>
    </button>
  );
}
